import React from 'react';
import { useDispatch } from 'react-redux';
import { Grid, List, ListItem, ListItemIcon, ListItemText, Radio, Typography } from '@mui/material';

import { SearchSelectField, SimpleTextField, useSettings, Utils } from 'app/main/components';
import { type DetailsSectionInfoContentProps } from 'app/main/components/page-layouts';

type ShippingAddressesListItemProps = {
	address: any;
	isSelected: boolean;
} & Pick<DetailsSectionShippingProps, 'module' | 'sendChange'>;

type DetailsSectionShippingProps = {
	module: string;
	editableAddress?: boolean;
} & Pick<DetailsSectionInfoContentProps, 'model' | 'isEdit' | 'sendChange'>;

function ShippingAddressesListItem({ module, address, isSelected, sendChange }: ShippingAddressesListItemProps) {
	const { settings, t } = useSettings(module);

	const address2 = Utils.formatAddress2({
		t,
		address: {
			zip: address.shad_zip,
			city: address.shad_city,
			district: address.shad_dist_iso,
			country: address.shad_ctry_title
		}
	});

	function handleChange() {
		sendChange(`${settings.modu_short}_shad_uid`, address.shad_uid);
		sendChange(`${settings.modu_short}_shad_title`, address.shad_title || null);
		sendChange(`${settings.modu_short}_shad_address`, address.shad_address);
		sendChange(`${settings.modu_short}_shad_city`, address.shad_city || null);
		sendChange(`${settings.modu_short}_shad_zip`, address.shad_zip || null);
		sendChange(`${settings.modu_short}_shad_dist_id`, address.shad_dist_id || null);
		sendChange(`${settings.modu_short}_shad_dist_iso`, address.shad_dist_iso || null);
		sendChange(`${settings.modu_short}_shad_regi_id`, address.shad_regi_id || null);
		sendChange(`${settings.modu_short}_shad_ctry_id`, address.shad_ctry_id || null);
		sendChange(`${settings.modu_short}_shad_ctry_title`, address.shad_ctry_title || null);

		if (address?.comp_shme_id) sendChange(`${settings.modu_short}_shme_id`, address.comp_shme_id);
		if (address?.comp_shag_id) sendChange(`${settings.modu_short}_shag_id`, address.comp_shag_id);
	}

	return (
		<ListItem disablePadding>
			<ListItemIcon>
				<Radio checked={isSelected} onClick={handleChange} />
			</ListItemIcon>
			<ListItemText
				slotProps={{
					primary: { className: 'font-400', variant: 'subtitle2' },
					secondary: { component: 'div' }
				}}
				primary={address.shad_title}
				secondary={
					<>
						<Typography className="font-400" variant="subtitle2" color="text.secondary">
							{address.shad_address || ''}
						</Typography>
						<Typography className="font-400" variant="subtitle2" color="text.secondary">
							{address2}
						</Typography>
					</>
				}
			/>
		</ListItem>
	);
}

export default function DetailsSectionShipping(props: DetailsSectionShippingProps) {
	const dispatch = useDispatch();
	const { settings, t } = useSettings(props.module);

	const defaultShippingAddress: any = {
		shad_title: props.model[`${settings.modu_short}_comp_title`],
		shad_address: props.model[`${settings.modu_short}_comp_address`],
		shad_city: props.model[`${settings.modu_short}_comp_city`],
		shad_zip: props.model[`${settings.modu_short}_comp_zip`],
		shad_dist_id: props.model[`${settings.modu_short}_comp_dist_id`],
		shad_dist_iso: props.model[`${settings.modu_short}_comp_dist_iso`],
		shad_regi_id: props.model[`${settings.modu_short}_comp_regi_id`],
		shad_ctry_id: props.model[`${settings.modu_short}_comp_ctry_id`],
		shad_ctry_title: props.model[`${settings.modu_short}_comp_ctry_title`]
	};

	const [shippingAddresses, setShippingAddresses] = React.useState([]);

	React.useEffect(() => {
		const companyUid = props.model[`${settings.modu_short}_comp_uid`];
		if (props.isEdit && !!companyUid)
			Utils.executeStored({
				action: 'st_companies_shippingaddresses_list',
				dispatch,
				params: {
					n_page: 1,
					n_rows: 20,
					filters: [{ filt_field: 'shad_comp_uid', filt_clause: 'equals', filt_value: companyUid }]
				},
				successHandler: response => setShippingAddresses(response.data.list || [])
			});
	}, [dispatch, props.isEdit, props.model, settings.modu_short]);

	const addressUid = props.model[`${settings.modu_short}_shad_uid`];
	const regionId = props.model[`${settings.modu_short}_shad_regi_id`];
	const countryId = props.model[`${settings.modu_short}_shad_ctry_id`];

	if (!props.isEdit || (shippingAddresses.length === 0 && !props.editableAddress)) {
		return (
			<Grid size={12}>
				<Typography className="font-400" variant="subtitle2">
					{props.model[`${settings.modu_short}_shad_title`]}
				</Typography>
				<Typography className="font-400" variant="subtitle2" color="text.secondary">
					{props.model[`${settings.modu_short}_shad_address`] || ''}
				</Typography>
				<Typography className="font-400" variant="subtitle2" color="text.secondary">
					{Utils.formatAddress2({
						t,
						address: {
							zip: props.model[`${settings.modu_short}_shad_zip`],
							city: props.model[`${settings.modu_short}_shad_city`],
							district: props.model[`${settings.modu_short}_shad_dist_iso`],
							country: props.model[`${settings.modu_short}_shad_ctry_title`]
						}
					})}
				</Typography>
			</Grid>
		);
	}

	return (
		<>
			{/* ADDRESSES LIST */}
			{shippingAddresses.length > 0 && (
				<Grid size={12}>
					<List disablePadding>
						{[defaultShippingAddress, ...shippingAddresses].map(address => (
							<ShippingAddressesListItem
								key={address.shad_uid || 'default'}
								module={props.module}
								address={address}
								isSelected={(address.shad_uid || 'default') === (addressUid || 'default')}
								sendChange={props.sendChange}
							/>
						))}
					</List>
				</Grid>
			)}
			{props.editableAddress && (
				<>
					{/* ADDRESS */}
					<Grid size={12}>
						<SimpleTextField
							name={`${settings.modu_short}_shad_address`}
							label={t('DETAILS_COMPANIES_FIELD_ADDRESS')}
							value={props.model[`${settings.modu_short}_shad_address`]}
							isEdit={props.isEdit}
							sendChange={props.sendChange}
						/>
					</Grid>
					{/* ADDRESS CITY */}
					<Grid size={{ sm: 6, xs: 12 }}>
						<SimpleTextField
							name={`${settings.modu_short}_shad_city`}
							label={t('DETAILS_COMPANIES_FIELD_ADDRESS_CITY')}
							value={props.model[`${settings.modu_short}_shad_city`]}
							isEdit={props.isEdit}
							sendChange={props.sendChange}
						/>
					</Grid>
					{/* ADDRESS ZIP */}
					<Grid size={{ sm: 6, xs: 12 }}>
						<SimpleTextField
							name={`${settings.modu_short}_shad_zip`}
							label={t('DETAILS_COMPANIES_FIELD_ADDRESS_ZIP')}
							value={props.model[`${settings.modu_short}_shad_zip`]}
							isEdit={props.isEdit}
							sendChange={props.sendChange}
						/>
					</Grid>
					{/* ADDRESS DISTRICT */}
					<Grid size={{ sm: 6, xs: 12 }}>
						<SearchSelectField
							name={`${settings.modu_short}_shad_dist_id`}
							label={t('DETAILS_COMPANIES_FIELD_ADDRESS_DISTRICT')}
							systable="districts"
							value={props.model[`${settings.modu_short}_shad_dist_id`]}
							filters={
								regionId
									? [{ filt_field: 'dist_regi_id', filt_clause: 'equals', filt_value: regionId }]
									: []
							}
							filterOptions={{ stringify: option => `${option.dist_iso} ${option.dist_title}` }}
							getOptionLabel={option => `${option.dist_iso} - ${option.dist_title}`}
							isEdit={props.isEdit}
							sendChange={props.sendChange}
							afterChange={value => {
								if (value) {
									props.sendChange(`${settings.modu_short}_shad_ctry_id`, value.regi_ctry_id);
									props.sendChange(`${settings.modu_short}_shad_regi_id`, value.dist_regi_id);
								}
							}}
						/>
					</Grid>
					{/* ADDRESS REGION */}
					<Grid size={{ sm: 6, xs: 12 }}>
						<SearchSelectField
							name={`${settings.modu_short}_shad_regi_id`}
							label={t('DETAILS_COMPANIES_FIELD_ADDRESS_REGION')}
							systable="regions"
							value={regionId}
							filters={
								countryId
									? [{ filt_field: 'regi_ctry_id', filt_clause: 'equals', filt_value: countryId }]
									: []
							}
							isEdit={props.isEdit}
							sendChange={props.sendChange}
							afterChange={value => {
								if (value) props.sendChange(`${settings.modu_short}_shad_ctry_id`, value.regi_ctry_id);
							}}
						/>
					</Grid>
					{/* ADDRESS COUNTRY */}
					<Grid size={{ sm: 6, xs: 12 }}>
						<SearchSelectField
							name={`${settings.modu_short}_shad_ctry_id`}
							label={t('DETAILS_COMPANIES_FIELD_ADDRESS_COUNTRY')}
							systable="countries"
							value={countryId}
							filterOptions={{ stringify: option => `${option.ctry_title} ${option.ctry_iso}` }}
							getOptionLabel={option => `${option.ctry_iso} - ${option.ctry_title}`}
							isEdit={props.isEdit}
							sendChange={props.sendChange}
						/>
					</Grid>
					{/* ADDRESS ZONE */}
					<Grid size={{ sm: 6, xs: 12 }}>
						<SearchSelectField
							name={`${settings.modu_short}_shad_zone_id`}
							label={t('DETAILS_COMPANIES_FIELD_ADDRESS_ZONE')}
							systable="zones"
							value={props.model[`${settings.modu_short}_shad_zone_id`]}
							isEdit={props.isEdit}
							sendChange={props.sendChange}
						/>
					</Grid>
				</>
			)}
		</>
	);
}
